/* eslint-disable */
import React, { useRef, useState, useCallback } from 'react';
import './index.less';
import './index.en.less';

export type LayoutType = 'standard' | 'simple' | 'case-study' | 'image-left';

export interface INumberedStepItem {
  text: string | React.ReactNode;
}

export interface INumberedStepsCarouselProps {
  type?: LayoutType;
  mainTitle?: string;
  subTitle?: string;
  description?: string;
  caseLabel?: string;
  caseTitle?: string;
  illustration?: React.ReactNode;
  items?: INumberedStepItem[];
  id?: string;
  defaultActive?: number;
  cardWidth?: number;
  cardHeight?: number;
  onComplete?: () => void;
}

const NumberedStepsCarousel: React.FunctionComponent<INumberedStepsCarouselProps> = ({
  type = 'standard',
  mainTitle,
  subTitle,
  description,
  caseLabel,
  caseTitle,
  illustration,
  items = [],
  id,
  defaultActive = 0,
  cardWidth,
  cardHeight,
  onComplete,
}) => {
  const [activeIndices, setActiveIndices] = useState<Set<number>>(
    () => new Set(items.map((_, index) => index))
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const scrollLeft = useRef(0);
  const hasMoved = useRef(false);
  const completedRef = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, items.length]);

  const scrollByDirection = (dir: 1 | -1) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    dragStartX.current = e.pageX - scrollContainerRef.current.offsetLeft;
    scrollLeft.current = scrollContainerRef.current.scrollLeft;
    hasMoved.current = false;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - dragStartX.current) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
    if (Math.abs(walk) > 5) {
      hasMoved.current = true;
    }
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (!completedRef.current && hasMoved.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [onComplete]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
    }
  }, [isDragging]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!scrollContainerRef.current) return;
    dragStartX.current = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    scrollLeft.current = scrollContainerRef.current.scrollLeft;
    hasMoved.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!scrollContainerRef.current) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - dragStartX.current) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
    if (Math.abs(walk) > 5) {
      hasMoved.current = true;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!completedRef.current && hasMoved.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [onComplete]);

  const renderHeader = () => {
    switch (type) {
      case 'simple':
        return (
          <div className="nsc-header nsc-header-simple">
            {mainTitle && <h2 className="nsc-title">{mainTitle}</h2>}
            {description && <p className="nsc-description">{description}</p>}
          </div>
        );
      case 'case-study':
        return (
          <div className="nsc-header nsc-header-case">
            <div className="nsc-case-header">
              {caseLabel && <span className="nsc-case-label">{caseLabel}</span>}
              {caseTitle && <h2 className="nsc-case-title">{caseTitle}</h2>}
            </div>
            {description && <p className="nsc-description">{description}</p>}
          </div>
        );
      case 'image-left':
        return (
          <div className="nsc-header nsc-header-image-left">
            {mainTitle && <h2 className="nsc-main-title">{mainTitle}</h2>}
            {subTitle && <h3 className="nsc-sub-title">{subTitle}</h3>}
            {description && <p className="nsc-description">{description}</p>}
          </div>
        );
      case 'standard':
      default:
        return (
          <div className="nsc-header nsc-header-standard">
            <div className="nsc-header-text">
              {mainTitle && <h2 className="nsc-main-title">{mainTitle}</h2>}
              {subTitle && <h3 className="nsc-sub-title">{subTitle}</h3>}
              {description && <p className="nsc-description">{description}</p>}
            </div>
            {illustration && <div className="nsc-illustration">{illustration}</div>}
          </div>
        );
    }
  };

  const cardStyle: React.CSSProperties = {};
  if (cardWidth) cardStyle.width = `${cardWidth}rem`;
  if (cardHeight) cardStyle.height = `${cardHeight}rem`;

  return (
    <div className={`numbered-steps-carousel nsc-type-${type}`} id={id}>
      {renderHeader()}
      <div className="nsc-content-wrapper">
        {type === 'image-left' && illustration && (
          <div className="nsc-left-illustration">{illustration}</div>
        )}
        <div className="nsc-scroll-area">
        <div
          ref={scrollContainerRef}
          className={`nsc-cards-scroll${isDragging ? ' is-dragging' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="nsc-cards-container">
            {items.map((item, index) => {
              const isActive = activeIndices.has(index);
              const handleCardClick = () => {
                if (hasMoved.current) return;
                setActiveIndices((prev) => {
                  const next = new Set(prev);
                  next.add(index);
                  return next;
                });
              };
              return (
                <div
                  key={index}
                  className={`nsc-card${isActive ? ' is-active' : ''}`}
                  style={cardStyle}
                  onClick={handleCardClick}
                >
                  <div className="nsc-card-number">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <div className="nsc-card-content">
                    <p className="nsc-card-text">
                      {typeof item.text === 'string' ? (
                        <span dangerouslySetInnerHTML={{ __html: item.text }} />
                      ) : (
                        item.text
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {canScrollLeft && (
          <button
            type="button"
            className="nsc-scroll-btn nsc-scroll-btn-left"
            aria-label="Scroll left"
            onClick={() => scrollByDirection(-1)}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        {canScrollRight && (
          <button
            type="button"
            className={`nsc-scroll-btn nsc-scroll-btn-right${
              !canScrollLeft ? ' nsc-scroll-hint' : ''
            }`}
            aria-label="Scroll right"
            onClick={() => scrollByDirection(1)}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.5 15L12.5 10L7.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(NumberedStepsCarousel);
